import apiClient from './apiClient';

// Define interface for API response
interface ApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
}

// Job role interface
export interface JobRole {
  _id: string;
  title: string;
  name?: string;
  description?: string;
  skills?: string[];
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Experience level interface
export interface ExperienceLevel {
  _id: string;
  name: string;
  order: number;
  description?: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Language interface
export interface Language {
  _id: string;
  name: string;
  code?: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Company interface
export interface Company {
  _id: string;
  name: string;
  description?: string;
  website?: string;
  industry?: string;
  logoUrl?: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Interview goal interface
export interface InterviewGoal {
  _id: string;
  name: string;
  description?: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Helper function to print the structure of a complex object for debugging
function debugLogObject(obj: any, name: string, maxLevel = 2, currentLevel = 0) {
  if (currentLevel > maxLevel) return;

  // console.log(`${'  '.repeat(currentLevel)}${name} type: ${typeof obj}`);

  if (obj === null) {
    // console.log(`${'  '.repeat(currentLevel)}${name} is null`);
    return;
  }

  if (typeof obj !== 'object') {
    // console.log(`${'  '.repeat(currentLevel)}${name} value: ${obj}`);
    return;
  }

  if (Array.isArray(obj)) {
    // console.log(`${indent}${name} is array with ${obj.length} elements`);
    if (obj.length > 0 && currentLevel < maxLevel) {
      debugLogObject(obj[0], `${name}[0]`, maxLevel, currentLevel + 1);
    }
    return;
  }

  const keys = Object.keys(obj);
  // console.log(`${indent}${name} is object with keys: ${keys.join(', ')}`);
  
  keys.forEach(key => {
    if (currentLevel < maxLevel) {
      debugLogObject(obj[key], `${name}.${key}`, maxLevel, currentLevel + 1);
    }
  });
}

// Helper function to extract data from API response with improved error logging
function extractDataFromResponse<T>(response: any, entityName: string): T[] {
  // console.log(`ConfigService: ${entityName} raw response:`, response);
  
  try {
    // NEW CASE: Handle detailed logging for a better understanding of the structure
    // console.log(`ConfigService: Beginning detailed analysis of ${entityName} response`);
    debugLogObject(response, entityName);
    
    // Case 1: If it's already an array, use it directly
    if (Array.isArray(response)) {
      // console.log(`ConfigService: ${entityName} is already an array with ${response.length} items`);
      return response;
    }
    
    // Case 2: If it has a nested data property that's an array
    if (response && response.data && Array.isArray(response.data)) {
      // console.log(`ConfigService: ${entityName} extracted from response.data with ${response.data.length} items`);
      return response.data;
    }
    
    // Case 3: If it's an object with a nested structure { statusCode, message, data }
    if (response && typeof response === 'object' && response.statusCode && response.data) {
      if (Array.isArray(response.data)) {
        // console.log(`ConfigService: ${entityName} extracted from ApiResponse.data with ${response.data.length} items`);
        return response.data;
      }
      
      // If response.data is an object with another data property (double nesting)
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        // console.log(`ConfigService: ${entityName} extracted from double-nested data with ${response.data.data.length} items`);
        return response.data.data;
      }
    }
    
    // Additional debugging for more specific structures
    if (response && typeof response === 'object') {
      // Handle common API response patterns
      
      // SPECIFIC CASE: Object with numeric keys and _response
      // This specifically handles the format we're getting from the API: {0: {...}, 1: {...}, _response: {...}}
      if (response._response && typeof response === 'object') {
        // console.log(`ConfigService: ${entityName} has _response property and possibly numeric keys`);
        
        // Check for numeric keys in the response
        const numericKeys = Object.keys(response).filter(key => 
          !isNaN(Number(key)) && key !== '_response'
        );
        
        if (numericKeys.length > 0) {
          const items = numericKeys.map(key => response[key]);
          // console.log(`ConfigService: ${entityName} extracted ${items.length} items from numeric keys`);
          return items as T[];
        }
        
        // Only return a single item if we couldn't find multiple numeric keys
        // This is a fallback for backward compatibility
        if (typeof response[0] === 'object') {
          // console.log(`ConfigService: ${entityName} falling back to single item at index 0`);
          return [response[0] as T];
        }
      }
      
      // Success flag pattern
      if (response.success === true && response.data) {
        // console.log(`ConfigService: ${entityName} has success flag and data property`);
        if (Array.isArray(response.data)) {
          // console.log(`ConfigService: ${entityName} extracted from success.data with ${response.data.length} items`);
          return response.data as T[];
        }
      }
      
      // Try to find any array property at the top level
      for (const key in response) {
        if (Array.isArray(response[key]) && response[key].length > 0) {
          // console.log(`ConfigService: ${entityName} found array in property '${key}' with ${response[key].length} items`);
          return response[key] as T[];
        }
      }
      
      // Try to find any array property one level deeper
      for (const key in response) {
        if (response[key] && typeof response[key] === 'object') {
          for (const nestedKey in response[key]) {
            if (Array.isArray(response[key][nestedKey]) && response[key][nestedKey].length > 0) {
              // console.log(`ConfigService: ${entityName} found array in nested property '${key}.${nestedKey}' with ${response[key][nestedKey].length} items`);
              return response[key][nestedKey] as T[];
            }
          }
        }
      }
    }
    
    // Log detailed structure if we couldn't extract data
    // console.warn(`ConfigService: Unexpected ${entityName} response format:`, response);
    // console.warn(`ConfigService: Response type: ${typeof response}`);
    if (response && typeof response === 'object') {
      console.warn(`ConfigService: Response keys: ${Object.keys(response).join(', ')}`);
      if (response.data) {
        console.warn(`ConfigService: response.data type: ${typeof response.data}`);
        console.warn(`ConfigService: response.data keys: ${Object.keys(response.data || {}).join(', ')}`);
      }
    }
    
    // Return empty array as fallback
    return [];
  } catch (error) {
    console.error(`ConfigService: Error processing ${entityName} response:`, error);
    return [];
  }
}

// Config service class
class ConfigService {
  // Cache storage
  private jobRolesCache: JobRole[] | null = null;
  private experienceLevelsCache: ExperienceLevel[] | null = null;
  private languagesCache: Language[] | null = null;
  private companiesCache: Company[] | null = null;
  private interviewGoalsCache: InterviewGoal[] | null = null;
  
  // Pending promises to prevent duplicate in-flight requests
  private jobRolesPromise: Promise<JobRole[]> | null = null;
  private experienceLevelsPromise: Promise<ExperienceLevel[]> | null = null;
  private languagesPromise: Promise<Language[]> | null = null;
  private companiesPromise: Promise<Company[]> | null = null;
  private interviewGoalsPromise: Promise<InterviewGoal[]> | null = null;
  
  // Cache timeout - 30 minutes
  private cacheTimeout = 30 * 60 * 1000;
  private lastCacheTime: Record<string, number> = {};

  // Fetch job roles
  async getJobRoles(): Promise<JobRole[]> {
    const cacheKey = 'jobRoles';
    // Check if cache has expired
    const now = Date.now();
    if (this.lastCacheTime[cacheKey] && now - this.lastCacheTime[cacheKey] > this.cacheTimeout) {
      // console.log('ConfigService: Job roles cache expired, clearing');
      this.jobRolesCache = null;
    }
    
    // Return cached data if available
    if (this.jobRolesCache) {
      // console.log(`ConfigService: Returning cached job roles (${this.jobRolesCache.length} items)`);
      return this.jobRolesCache;
    }
    
    // If a request is already in progress, return that promise
    if (this.jobRolesPromise) {
      // console.log('ConfigService: Returning pending job roles request');
      return this.jobRolesPromise;
    }
    
    // Otherwise, make a new request
    // console.log('ConfigService: Fetching job roles from API...');
    this.jobRolesPromise = this.fetchJobRoles();
    
    try {
      const data = await this.jobRolesPromise;
      // Store result in cache
      this.jobRolesCache = data;
      this.lastCacheTime[cacheKey] = Date.now();
      return data;
    } finally {
      // Clear the promise so subsequent requests will make a new one if needed
      this.jobRolesPromise = null;
    }
  }
  
  private async fetchJobRoles(): Promise<JobRole[]> {
    try {
      const response = await apiClient.get<ApiResponse<JobRole[]>>('/config/job-roles');
      return extractDataFromResponse<JobRole>(response, 'job roles');
    } catch (error) {
      console.error('ConfigService: Failed to fetch job roles:', error);
      return [];
    }
  }

  // Fetch experience levels
  async getExperienceLevels(): Promise<ExperienceLevel[]> {
    const cacheKey = 'experienceLevels';
    // Check if cache has expired
    const now = Date.now();
    if (this.lastCacheTime[cacheKey] && now - this.lastCacheTime[cacheKey] > this.cacheTimeout) {
      // console.log('ConfigService: Experience levels cache expired, clearing');
      this.experienceLevelsCache = null;
    }
    
    // Return cached data if available
    if (this.experienceLevelsCache) {
      // console.log(`ConfigService: Returning cached experience levels (${this.experienceLevelsCache.length} items)`);
      return this.experienceLevelsCache;
    }
    
    // If a request is already in progress, return that promise
    if (this.experienceLevelsPromise) {
      // console.log('ConfigService: Returning pending experience levels request');
      return this.experienceLevelsPromise;
    }
    
    // Otherwise, make a new request
    // console.log('ConfigService: Fetching experience levels from API...');
    this.experienceLevelsPromise = this.fetchExperienceLevels();
    
    try {
      const data = await this.experienceLevelsPromise;
      // Store result in cache
      this.experienceLevelsCache = data;
      this.lastCacheTime[cacheKey] = Date.now();
      return data;
    } finally {
      // Clear the promise so subsequent requests will make a new one if needed
      this.experienceLevelsPromise = null;
    }
  }
  
  private async fetchExperienceLevels(): Promise<ExperienceLevel[]> {
    try {
      const response = await apiClient.get<ApiResponse<ExperienceLevel[]>>('/config/experience-levels');
      return extractDataFromResponse<ExperienceLevel>(response, 'experience levels');
    } catch (error) {
      console.error('ConfigService: Failed to fetch experience levels:', error);
      return [];
    }
  }

  // Fetch languages
  async getLanguages(): Promise<Language[]> {
    const cacheKey = 'languages';
    // Check if cache has expired
    const now = Date.now();
    if (this.lastCacheTime[cacheKey] && now - this.lastCacheTime[cacheKey] > this.cacheTimeout) {
      // console.log('ConfigService: Languages cache expired, clearing');
      this.languagesCache = null;
    }
    
    // Return cached data if available
    if (this.languagesCache) {
      // console.log(`ConfigService: Returning cached languages (${this.languagesCache.length} items)`);
      return this.languagesCache;
    }
    
    // If a request is already in progress, return that promise
    if (this.languagesPromise) {
      // console.log('ConfigService: Returning pending languages request');
      return this.languagesPromise;
    }
    
    // Otherwise, make a new request
    // console.log('ConfigService: Fetching languages from API...');
    this.languagesPromise = this.fetchLanguages();
    
    try {
      const data = await this.languagesPromise;
      // Store result in cache
      this.languagesCache = data;
      this.lastCacheTime[cacheKey] = Date.now();
      return data;
    } finally {
      // Clear the promise so subsequent requests will make a new one if needed
      this.languagesPromise = null;
    }
  }
  
  private async fetchLanguages(): Promise<Language[]> {
    try {
      const response = await apiClient.get<ApiResponse<Language[]>>('/config/languages');
      return extractDataFromResponse<Language>(response, 'languages');
    } catch (error) {
      console.error('ConfigService: Failed to fetch languages:', error);
      return [];
    }
  }

  // Fetch companies
  async getCompanies(): Promise<Company[]> {
    const cacheKey = 'companies';
    // Check if cache has expired
    const now = Date.now();
    if (this.lastCacheTime[cacheKey] && now - this.lastCacheTime[cacheKey] > this.cacheTimeout) {
      // console.log('ConfigService: Companies cache expired, clearing');
      this.companiesCache = null;
    }
    
    // Return cached data if available
    if (this.companiesCache) {
      // console.log(`ConfigService: Returning cached companies (${this.companiesCache.length} items)`);
      return this.companiesCache;
    }
    
    // If a request is already in progress, return that promise
    if (this.companiesPromise) {
      // console.log('ConfigService: Returning pending companies request');
      return this.companiesPromise;
    }
    
    // Otherwise, make a new request
    // console.log('ConfigService: Fetching companies from API...');
    this.companiesPromise = this.fetchCompanies();
    
    try {
      const data = await this.companiesPromise;
      // Store result in cache
      this.companiesCache = data;
      this.lastCacheTime[cacheKey] = Date.now();
      return data;
    } finally {
      // Clear the promise so subsequent requests will make a new one if needed
      this.companiesPromise = null;
    }
  }
  
  private async fetchCompanies(): Promise<Company[]> {
    try {
      const response = await apiClient.get<ApiResponse<Company[]>>('/config/companies');
      return extractDataFromResponse<Company>(response, 'companies');
    } catch (error) {
      console.error('ConfigService: Failed to fetch companies:', error);
      return [];
    }
  }

  // Fetch interview goals
  async getInterviewGoals(): Promise<InterviewGoal[]> {
    const cacheKey = 'interviewGoals';
    // Check if cache has expired
    const now = Date.now();
    if (this.lastCacheTime[cacheKey] && now - this.lastCacheTime[cacheKey] > this.cacheTimeout) {
      // console.log('ConfigService: Interview goals cache expired, clearing');
      this.interviewGoalsCache = null;
    }
    
    // Return cached data if available
    if (this.interviewGoalsCache) {
      // console.log(`ConfigService: Returning cached interview goals (${this.interviewGoalsCache.length} items)`);
      return this.interviewGoalsCache;
    }
    
    // If a request is already in progress, return that promise
    if (this.interviewGoalsPromise) {
      // console.log('ConfigService: Returning pending interview goals request');
      return this.interviewGoalsPromise;
    }
    
    // Otherwise, make a new request
    // console.log('ConfigService: Fetching interview goals from API...');
    this.interviewGoalsPromise = this.fetchInterviewGoals();
    
    try {
      const data = await this.interviewGoalsPromise;
      // Store result in cache
      this.interviewGoalsCache = data;
      this.lastCacheTime[cacheKey] = Date.now();
      return data;
    } finally {
      // Clear the promise so subsequent requests will make a new one if needed
      this.interviewGoalsPromise = null;
    }
  }
  
  private async fetchInterviewGoals(): Promise<InterviewGoal[]> {
    try {
      const response = await apiClient.get<ApiResponse<InterviewGoal[]>>('/config/interview-goals');
      return extractDataFromResponse<InterviewGoal>(response, 'interview goals');
    } catch (error) {
      console.error('ConfigService: Failed to fetch interview goals:', error);
      return [];
    }
  }
  
  // Method to clear the cache if needed (e.g., for forced refresh)
  clearCache() {
    this.jobRolesCache = null;
    this.experienceLevelsCache = null;
    this.languagesCache = null;
    this.companiesCache = null;
    this.interviewGoalsCache = null;
    this.lastCacheTime = {};
    // console.log('ConfigService: Cache cleared');
  }
}

// Export an instance of the service
const configService = new ConfigService();
export default configService; 